import jsPDF from 'jspdf';

/**
 * Generates a simple, clean, professional PDF report containing details above the table
 * and the filtered Service Orders matching active filters.
 * 
 * @param {Array} orders - Array of filtered service order objects
 * @param {Object} appliedFilters - Current applied filter object
 * @param {Object} optionsMap - Maps containing dropdown options for human-readable labels { statusOptions, priorityOptions, companyOptions, technicianOptions }
 * @param {string} searchQuery - Active search query string
 */
export const exportFilteredServiceOrdersPDF = (orders = [], appliedFilters = {}, optionsMap = {}, searchQuery = '') => {
    if (!orders || orders.length === 0) {
        throw new Error('No service orders match the applied filters to export.');
    }

    // Initialize Landscape A4 jsPDF instance (841.89 pt x 595.28 pt)
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();   // 841.89
    const pageHeight = doc.internal.pageSize.getHeight(); // 595.28

    const marginX = 40;
    const usableWidth = pageWidth - (marginX * 2); // 761.89 pt

    // Colors: Clean black and neutral greys (Simple, professional PDF styling)
    const textBlack = [0, 0, 0];
    const textMuted = [80, 80, 80];
    const lineGrey = [180, 180, 180];
    const headerBg = [242, 242, 242];

    // Helper: Top Header
    const drawHeader = (pageNumber, totalPages) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(14);
        doc.setTextColor(...textBlack);
        doc.text('KQSPL QUALITY SERVICES PVT. LTD.', marginX, 30);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...textMuted);
        const nowStr = new Date().toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
        doc.text(`Generated: ${nowStr}`, pageWidth - marginX, 30, { align: 'right' });

        // Simple Top Divider Line
        doc.setDrawColor(...lineGrey);
        doc.setLineWidth(0.75);
        doc.line(marginX, 38, pageWidth - marginX, 38);
    };

    // Helper: Bottom Footer
    const drawFooter = (pageNumber, totalPages) => {
        doc.setDrawColor(...lineGrey);
        doc.setLineWidth(0.5);
        doc.line(marginX, pageHeight - 30, pageWidth - marginX, pageHeight - 30);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...textMuted);
        doc.text('KQSPL Service Orders Report', marginX, pageHeight - 16);
        doc.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - marginX, pageHeight - 16, { align: 'right' });
    };

    // Resolve human-readable filter details
    const filterDetails = [];
    if (searchQuery) {
        filterDetails.push(`Search Query: "${searchQuery}"`);
    }

    if (appliedFilters.company) {
        const found = (optionsMap.companyOptions || []).find(o => String(o.id) === String(appliedFilters.company));
        filterDetails.push(`Company: ${found ? found.name : appliedFilters.company}`);
    }

    if (appliedFilters.status) {
        const found = (optionsMap.statusOptions || []).find(o => String(o.id) === String(appliedFilters.status));
        filterDetails.push(`Status: ${found ? found.name : appliedFilters.status}`);
    }

    if (appliedFilters.priority) {
        const found = (optionsMap.priorityOptions || []).find(o => String(o.id) === String(appliedFilters.priority));
        filterDetails.push(`Priority: ${found ? found.name : appliedFilters.priority}`);
    }

    if (appliedFilters.technician) {
        const found = (optionsMap.technicianOptions || []).find(o => String(o.id) === String(appliedFilters.technician));
        const techLabel = found ? `${found.first_name || ''} ${found.last_name || ''}`.trim() || found.username : appliedFilters.technician;
        filterDetails.push(`Technician: ${techLabel}`);
    }

    if (appliedFilters.scheduled_at__gte) {
        filterDetails.push(`From Date: ${appliedFilters.scheduled_at__gte}`);
    }

    if (appliedFilters.scheduled_at__lte) {
        filterDetails.push(`To Date: ${appliedFilters.scheduled_at__lte}`);
    }

    if (appliedFilters.is_sub_service_order === 'true') {
        filterDetails.push(`Type: Sub-Service Orders`);
    } else if (appliedFilters.is_sub_service_order === 'false') {
        filterDetails.push(`Type: Parent Service Orders`);
    }

    // Simple Details Section above table
    let yPos = 56;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...textBlack);
    doc.text('SERVICE ORDERS FILTERED REPORT', marginX, yPos);
    yPos += 15;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...textMuted);

    if (filterDetails.length > 0) {
        doc.text(`Applied Filters:   ${filterDetails.join('   |   ')}`, marginX, yPos);
        yPos += 14;
    }

    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...textBlack);
    doc.text(`Total Records: ${orders.length}`, marginX, yPos);
    yPos += 18;

    // Table Column Definitions & Widths (Total usable = 761.89 pt)
    const columns = [
        { header: 'Order ID', width: 85 },
        { header: 'REQ ID', width: 60 },
        { header: 'Service Type', width: 100 },
        { header: 'Company', width: 165 },
        { header: 'Status', width: 75 },
        { header: 'Priority', width: 65 },
        { header: 'Technician', width: 110 },
        { header: 'Scheduled Date', width: 101 }
    ];

    const rowHeight = 20;
    const headerRowHeight = 22;

    const drawTableHeader = (y) => {
        doc.setFillColor(...headerBg);
        doc.rect(marginX, y, usableWidth, headerRowHeight, 'F');

        doc.setDrawColor(...textBlack);
        doc.setLineWidth(0.75);
        doc.line(marginX, y, marginX + usableWidth, y);
        doc.line(marginX, y + headerRowHeight, marginX + usableWidth, y + headerRowHeight);

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...textBlack);

        let currentX = marginX + 6;
        columns.forEach((col) => {
            doc.text(col.header, currentX, y + 15);
            currentX += col.width;
        });
    };

    drawTableHeader(yPos);
    yPos += headerRowHeight;

    // Helper to safely extract name from string, number, or object
    const extractName = (val) => {
        if (val === null || val === undefined) return '';
        if (typeof val === 'string' || typeof val === 'number') return String(val);
        if (typeof val === 'object') {
            return val.name || val.display_name || val.title || val.code || '';
        }
        return '';
    };

    // Helper to trim cell text nicely
    const formatCell = (text, maxLen) => {
        if (text === null || text === undefined || text === '') return '-';
        const str = String(text).trim();
        return str.length > maxLen ? str.substring(0, maxLen - 2) + '…' : str;
    };

    doc.setFontSize(8.5);

    orders.forEach((order, idx) => {
        // Page break check
        if (yPos + rowHeight > pageHeight - 40) {
            doc.addPage();
            yPos = 56;
            drawTableHeader(yPos);
            yPos += headerRowHeight;
        }

        // Simple row divider line
        doc.setDrawColor(...lineGrey);
        doc.setLineWidth(0.5);
        doc.line(marginX, yPos + rowHeight, marginX + usableWidth, yPos + rowHeight);

        doc.setTextColor(...textBlack);

        const orderId = order.display_name || order.name || `SO-${order.id}`;
        const reqId = order.req_id || '-';
        
        const serviceType = extractName(order.service_type) || extractName(order.service_type_details) || extractName(order.service_type_name) || '-';
        const companyName = extractName(order.company) || extractName(order.company_details) || extractName(order.company_name) || '-';
        const statusName = extractName(order.current_state) || extractName(order.status) || extractName(order.current_state_details) || extractName(order.status_details) || extractName(order.current_state_name) || extractName(order.status_name) || '-';
        const priorityName = extractName(order.priority) || extractName(order.priority_details) || extractName(order.priority_name) || '-';

        let techName = 'Unassigned';
        const techObj = order.technician_details || (typeof order.technician === 'object' ? order.technician : null);
        if (techObj) {
            const fullName = `${techObj.first_name || ''} ${techObj.last_name || ''}`.trim();
            techName = fullName || techObj.username || techObj.email || 'Unassigned';
        } else if (typeof order.technician_name === 'string' && order.technician_name) {
            techName = order.technician_name;
        } else if (typeof order.technician === 'string' && order.technician) {
            techName = order.technician;
        }

        const scheduled = order.scheduled_at 
            ? new Date(order.scheduled_at).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : 'Not scheduled';

        let currentX = marginX + 6;
        const cellY = yPos + 14;

        // Order ID
        doc.setFont('helvetica', 'bold');
        doc.text(formatCell(orderId, 16), currentX, cellY);
        currentX += columns[0].width;

        // REQ ID
        doc.setFont('helvetica', 'normal');
        doc.text(formatCell(reqId, 12), currentX, cellY);
        currentX += columns[1].width;

        // Service Type
        doc.text(formatCell(serviceType, 20), currentX, cellY);
        currentX += columns[2].width;

        // Company
        doc.text(formatCell(companyName, 32), currentX, cellY);
        currentX += columns[3].width;

        // Status
        doc.setFont('helvetica', 'bold');
        doc.text(formatCell(statusName, 15), currentX, cellY);
        currentX += columns[4].width;

        // Priority
        doc.setFont('helvetica', 'normal');
        doc.text(formatCell(priorityName, 12), currentX, cellY);
        currentX += columns[5].width;

        // Technician
        doc.text(formatCell(techName, 22), currentX, cellY);
        currentX += columns[6].width;

        // Scheduled Date
        doc.text(formatCell(scheduled, 18), currentX, cellY);

        yPos += rowHeight;
    });

    // Stamp Header and Footer on every page
    const totalPages = doc.internal.getNumberOfPages();
    for (let p = 1; p <= totalPages; p++) {
        doc.setPage(p);
        drawHeader(p, totalPages);
        drawFooter(p, totalPages);
    }

    // Trigger Browser Download
    const fileDate = new Date().toISOString().split('T')[0];
    doc.save(`Filtered_Service_Orders_${fileDate}.pdf`);
};
